import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, CalendarCheck, Route, GraduationCap, Smartphone, Clock, Star,
  Bell, Briefcase, CheckCircle2, ChevronRight, BadgeCheck,
} from "lucide-react";
import { Reveal, SectionHead, Field } from "../components/ui-bits";
import { useApp } from "../context/AppContext";
import { sendOtp, verifyOtp, submitPartner, formatApiError } from "../lib/api";
import { IMAGES } from "../config/images";
import { PARTNER_VALUES, PARTNER_STEPS, PARTNER_URGENCY, PARTNER_EARNING_NOTE } from "../config/defaults";

const ICONS = { calendar: CalendarCheck, route: Route, graduation: GraduationCap, smartphone: Smartphone, clock: Clock, star: Star };

export default function PartnersPage() {
  const formRef = useRef(null);
  const goForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <main className="pt-[68px] pb-28 lg:pb-0" data-testid="partners-page">
      <PartnerHero onCta={goForm} />
      <PartnerValues />
      <PartnerUrgency onCta={goForm} />
      <PartnerSteps />
      <DashboardPreview />
      <PartnerForm refEl={formRef} />
      <PartnerStickyBar onCta={goForm} />
    </main>
  );
}

function PartnerHero({ onCta }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  return (
    <header ref={ref} className="relative overflow-hidden" data-testid="partner-hero">
      <div className="dot-grid pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full" style={{ background: "radial-gradient(circle, rgba(124,77,255,0.22), transparent 70%)" }} />
      <div className="container-x relative grid grid-cols-1 items-center gap-12 py-14 lg:grid-cols-12 lg:py-20">
        <div className="lg:col-span-7">
          <Reveal><span className="eyebrow">Partner With Carza</span></Reveal>
          <h1 className="mt-6 max-w-2xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-paper sm:text-5xl lg:text-[3.6rem]" data-testid="partner-headline">
            {["Stop Running for Kilometres.", "Start Earning With CARZA."].map((t, i) => (
              <span key={t} className="block overflow-hidden pb-1">
                <motion.span initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] }} className={`block ${i === 1 ? "text-carza-lite text-glow" : ""}`}>
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-mist sm:text-lg" data-testid="partner-subheading">
              Get access to nearby car-care bookings, training, digital tools and a growing customer network.
            </p>
          </Reveal>
          <Reveal delay={0.6} className="mt-9 flex flex-wrap gap-4">
            <button onClick={onCta} className="btn btn-primary px-7 py-4 text-[15px]" data-testid="partner-become-cta">
              Become a CARZA Partner <ArrowRight size={16} />
            </button>
            <a href="#partner-steps" onClick={(e) => { e.preventDefault(); document.getElementById("partner-steps")?.scrollIntoView({ behavior: "smooth" }); }} className="btn btn-outline px-7 py-4 text-[15px]" data-testid="partner-how-cta">
              See How It Works
            </a>
          </Reveal>
        </div>
        <div className="relative lg:col-span-5">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.4 }} className="relative mx-auto max-w-[420px]">
            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-3xl border border-white/10">
              <img src={IMAGES.partnerIndia} alt="A CARZA partner in uniform servicing a car in an Indian residential society" className="aspect-[4/5] w-full object-cover" loading="eager" fetchPriority="high" data-testid="partner-hero-image" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(200deg, transparent 40%, rgba(19,8,38,0.6) 100%)" }} />
            </motion.div>
            {[
              { icon: CalendarCheck, label: "More Bookings", cls: "left-3 top-8" },
              { icon: GraduationCap, label: "Training & Support", cls: "right-3 top-1/3" },
              { icon: Clock, label: "Flexible Work", cls: "bottom-10 left-8" },
            ].map(({ icon: Icon, label, cls }, i) => (
              <div key={label} className={`absolute ${cls}`}>
                <div className="glass float-y flex items-center gap-2 rounded-2xl px-3.5 py-2.5" style={{ animationDelay: `${i * 0.7}s` }}>
                  <Icon size={14} className="text-carza-lite" />
                  <span className="text-[12px] font-semibold text-paper">{label}</span>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </header>
  );
}

function PartnerValues() {
  return (
    <section className="relative py-20 lg:py-24" data-testid="partner-values">
      <div className="container-x">
        <SectionHead
          eyebrow="Why Partner With Us"
          title={<>Built to Grow Your Service Business.</>}
          sub="CARZA handles demand, training and digital tools — you focus on great service."
        />
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PARTNER_VALUES.map((v, i) => {
            const Icon = ICONS[v.icon] || Star;
            return (
              <Reveal key={v.title} delay={(i % 3) * 0.06}>
                <div className="card-surface flex h-full items-start gap-4 rounded-3xl p-6" data-testid={`partner-value-${i}`}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-carza-dim text-carza-lite"><Icon size={20} /></span>
                  <div>
                    <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-carza-lite">{v.title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-paper/90">{v.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PartnerUrgency({ onCta }) {
  return (
    <section className="relative overflow-hidden py-20 lg:py-24" data-testid="partner-urgency">
      <div className="absolute inset-0" style={{ background: "radial-gradient(120% 140% at 50% 0%, rgba(124,77,255,0.2) 0%, rgba(26,14,51,1) 65%)" }} />
      <div className="container-x relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal><span className="eyebrow">Why Join Carza Now?</span></Reveal>
          <Reveal delay={0.06}>
            <h2 className="mt-5 max-w-xl font-display text-3xl font-bold leading-tight tracking-tight text-paper sm:text-4xl">
              The network is growing. Early partners grow with it.
            </h2>
          </Reveal>
          <div className="mt-8 flex flex-col gap-3.5">
            {PARTNER_URGENCY.map((line, i) => (
              <Reveal key={line} delay={0.1 + i * 0.05}>
                <p className="flex items-start gap-3 text-[15px] text-paper/90">
                  <BadgeCheck size={18} className="mt-0.5 shrink-0 text-carza-lite" /> {line}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15} className="lg:col-span-5 lg:pl-6">
          <div className="glass rounded-3xl p-8" data-testid="partner-earning-note">
            <Star size={22} className="text-carza-lite" />
            <p className="mt-4 text-[15px] leading-relaxed text-paper">{PARTNER_EARNING_NOTE}</p>
            <button onClick={onCta} className="btn btn-primary mt-7 w-full py-4" data-testid="partner-apply-cta">
              Start Your Partner Application <ArrowRight size={15} />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PartnerSteps() {
  return (
    <section id="partner-steps" className="relative py-20 lg:py-24" data-testid="partner-steps">
      <div className="container-x">
        <SectionHead eyebrow="Onboarding" title={<>Four Steps to Your First Booking.</>} align="center" />
        <div className="relative mt-14 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          <div className="pointer-events-none absolute left-0 right-0 top-7 hidden border-t border-dashed border-white/10 lg:block" aria-hidden="true" />
          {PARTNER_STEPS.map((s, i) => (
            <Reveal key={s.no} delay={i * 0.08} className="relative">
              <div className="flex flex-col items-start gap-5" data-testid={`partner-step-${s.no}`}>
                <motion.span whileHover={{ rotate: 90, transition: { duration: 0.4 } }} className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border border-carza/40 bg-ink font-mono text-sm font-semibold text-carza-lite">
                  {s.no}
                </motion.span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-paper">{s.title}</h3>
                  <p className="mt-2 max-w-[240px] text-sm leading-relaxed text-mist">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function DashboardPreview() {
  return (
    <section className="relative pb-20 lg:pb-24" data-testid="partner-dashboard-preview">
      <div className="container-x">
        <SectionHead eyebrow="Your Digital Toolkit" title={<>Manage Everything From One Dashboard.</>} sub="Bookings, service area, availability and earnings — all digital. Below is a demonstration with sample data." align="center" />
        <Reveal delay={0.1} className="mx-auto mt-12 max-w-4xl">
          <div className="relative rounded-[1.75rem] border border-white/10 bg-gradient-to-b from-graphite-2 to-graphite p-5 sm:p-8" data-testid="partner-dashboard-mock">
            <span className="absolute -top-3 right-8 rounded-full border border-amber-400/40 bg-amber-400/15 px-3.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-amber-300">
              Demo preview — sample data
            </span>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[
                { label: "Today's Bookings", value: "3" },
                { label: "Upcoming Jobs", value: "5" },
                { label: "Completed Jobs", value: "132" },
                { label: "Customer Rating", value: "4.9 ★" },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`dash-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-mist-2">{s.label}</p>
                  <p className="mt-2 font-display text-2xl font-bold text-paper">{s.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 lg:col-span-2">
                <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-mist-2">Today's schedule</p>
                <div className="mt-4 flex flex-col gap-2.5">
                  {[
                    { time: "09:00 AM", job: "Hatchback · One Time wash", area: "Sector 45 · 2.1 km" },
                    { time: "11:00 AM", job: "Sedan · 15-day plan", area: "Green Residency · 1.4 km" },
                    { time: "03:00 PM", job: "SUV · One Time wash", area: "Palm Court Society · 3.0 km" },
                  ].map((j) => (
                    <div key={j.time} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3" data-testid="dash-job-row">
                      <span className="font-mono text-xs text-carza-lite">{j.time}</span>
                      <span className="flex-1 text-[13px] text-paper">{j.job}</span>
                      <span className="flex items-center gap-1 text-[11px] text-mist"><MapPinDot /> {j.area}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-mist-2">Availability</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-sm text-paper">Accepting bookings</span>
                    <span className="relative h-6 w-11 rounded-full bg-carza/80"><span className="absolute right-0.5 top-0.5 h-5 w-5 rounded-full bg-white" /></span>
                  </div>
                  <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.18em] text-mist-2">Service area</p>
                  <p className="mt-1.5 text-sm text-paper">South Delhi · Noida</p>
                </div>
                <div className="flex-1 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <p className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.18em] text-mist-2"><Bell size={11} /> Notifications</p>
                  <p className="mt-3 text-[13px] leading-relaxed text-mist">New booking near you · 1.2 km away</p>
                  <p className="mt-2 text-[13px] leading-relaxed text-mist">Training module: Interior care SOP</p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const MapPinDot = () => <span className="inline-block h-1.5 w-1.5 rounded-full bg-carza" />;

const PartnerForm = ({ refEl }) => {
  const [data, setData] = useState({ full_name: "", phone: "", city: "", service_area: "", experience: "", equipment: "", company_website: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const [otp, setOtp] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
  const formValid = Object.entries(data).every(([k, v]) => (k === "company_website" ? true : v.trim().length >= 1)) && data.full_name.trim().length >= 2 && data.phone.replace(/\D/g, "").length === 10;

  const sendOtpStep = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await sendOtp(data.phone, "partner");
      setDevOtp(res.dev_otp || "");
      setOtpSent(true);
      toast.success("OTP generated (demo mode — shown below)");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const verifyAndSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await verifyOtp(data.phone, otp, "partner");
      await submitPartner({ ...data, verify_token: res.verify_token });
      setDone(true);
      toast.success("Application received. The CARZA onboarding team will call you.");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={refEl} className="relative py-16 lg:py-24" data-testid="partner-form-section">
      <div className="container-x max-w-3xl">
        <Reveal>
          <div className="card-surface rounded-[2rem] p-6 sm:p-10" data-testid="partner-form-card">
            {done ? (
              <div className="flex flex-col items-center gap-4 py-10 text-center" data-testid="partner-success">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-carza-dim text-carza-lite"><CheckCircle2 size={26} /></span>
                <h3 className="font-display text-2xl font-bold text-paper">Application received</h3>
                <p className="max-w-sm text-sm text-mist">Thank you, {data.full_name.split(" ")[0]}. Our onboarding team will call you to walk you through verification, training and next steps.</p>
              </div>
            ) : !otpSent ? (
              <form onSubmit={(e) => { e.preventDefault(); sendOtpStep(); }} className="flex flex-col gap-4">
                <h3 className="font-display text-2xl font-bold text-paper">Become a CARZA Partner</h3>
                <p className="text-sm text-mist">Register in under 2 minutes. We verify your number first — no long forms.</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Full Name"><input className="field" value={data.full_name} onChange={set("full_name")} required placeholder="Your full name" autoComplete="name" data-testid="partner-name-input" /></Field>
                  <Field label="Mobile Number">
                    <div className="flex items-center gap-2 rounded-xl border px-4" style={{ borderColor: "var(--line)", background: "#180b30" }}>
                      <span className="border-r border-white/10 pr-2 text-sm text-paper">🇮🇳 +91</span>
                      <input className="w-full bg-transparent py-3 text-sm text-paper outline-none placeholder:text-mist-2/70" value={data.phone} onChange={set("phone")} required placeholder="10-digit number" inputMode="numeric" data-testid="partner-phone-input" />
                    </div>
                  </Field>
                  <Field label="City"><input className="field" value={data.city} onChange={set("city")} required placeholder="City you work in" data-testid="partner-city-input" /></Field>
                  <Field label="Preferred Service Area"><input className="field" value={data.service_area} onChange={set("service_area")} required placeholder="e.g. South Delhi, Gurugram" data-testid="partner-area-input" /></Field>
                  <Field label="Experience"><input className="field" value={data.experience} onChange={set("experience")} required inputMode="numeric" placeholder="Years of experience" data-testid="partner-experience-input" /></Field>
                  <Field label="Vehicle / Equipment"><input className="field" value={data.equipment} onChange={set("equipment")} required placeholder="Bike, car, cleaning kit…" data-testid="partner-equipment-input" /></Field>
                </div>
                <input type="text" name="company_website" value={data.company_website} onChange={set("company_website")} className="hidden" tabIndex={-1} aria-hidden="true" autoComplete="off" />
                {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="partner-error">{error}</p>}
                <button type="submit" disabled={!formValid || loading} className="btn btn-primary w-full py-4 disabled:opacity-40" data-testid="partner-send-otp-btn">
                  {loading ? "Generating OTP…" : <>Verify Number &amp; Apply <ChevronRight size={15} /></>}
                </button>
              </form>
            ) : (
              <div className="flex flex-col gap-4" data-testid="partner-otp-step">
                <h3 className="font-display text-2xl font-bold text-paper">Verify your mobile</h3>
                <p className="text-sm text-mist">OTP sent to +91 {data.phone}. <button onClick={() => setOtpSent(false)} className="text-carza-lite underline">Edit</button></p>
                <input className="field text-center font-mono text-xl tracking-[0.5em]" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" inputMode="numeric" data-testid="partner-otp-input" />
                {devOtp && <p className="rounded-xl border border-amber-400/25 bg-amber-400/10 px-4 py-3 text-center font-mono text-xs text-amber-200">Demo mode — your OTP is {devOtp}</p>}
                {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="partner-otp-error">{error}</p>}
                <button onClick={verifyAndSubmit} disabled={otp.length < 4 || loading} className="btn btn-primary w-full py-4 disabled:opacity-40" data-testid="partner-verify-submit-btn">
                  {loading ? "Submitting…" : "Submit Application"}
                </button>
              </div>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 text-center text-[11px] leading-relaxed text-mist-2" data-testid="partner-disclaimer">
            {PARTNER_EARNING_NOTE} CARZA does not offer guaranteed income.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

function PartnerStickyBar({ onCta }) {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-white/10 bg-ink/85 px-4 py-3 backdrop-blur-xl lg:hidden"
      style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}
      data-testid="partner-sticky-bar"
    >
      <Briefcase size={18} className="ml-2 shrink-0 text-carza-lite" />
      <button onClick={onCta} className="btn btn-primary h-12 flex-1 text-[15px]" data-testid="partner-sticky-cta">
        Become a Partner
      </button>
    </div>
  );
}
